// What a report had not saved when an expired session sent the tab to sign in
// again (caiet UC50-13, T060; docs/shape-ops-draft-session-handoff.md).
//
// The tab's sessionStorage, not localStorage: it survives the redirect, the MPass
// round trip and a reload, and goes with the tab. Not bound to the person, since
// that would put the IDNP in the browser (CF-63); a draft is its location's
// (CF-03), and an entry is replayed only into a draft or location the current
// session has loaded (§D4).
import type { DraftDefaultReasons } from './api';
import type { RowOperation } from './use-draft';

const STORAGE_KEY = 'simsm.reporting.unsaved';

export interface DraftHandOff {
  /** The draft on screen; null while its first rows still wait for their create. */
  draftId: string | null;
  /** Its location; null for rows entered before any location was chosen. */
  siteId: string | null;
  /** The R7 operations the server has not confirmed, in the order they were made. */
  operations: RowOperation[];
  defaultReasons: DraftDefaultReasons | null;
}

function sameTarget(a: DraftHandOff, b: DraftHandOff): boolean {
  return a.draftId === b.draftId && a.siteId === b.siteId;
}

function read(): DraftHandOff[] {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as DraftHandOff[]) : [];
  } catch {
    return [];
  }
}

function write(entries: DraftHandOff[]): void {
  try {
    if (entries.length === 0) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* storage refused (private mode, quota): the redirect still happens, and
       the edits are lost as they were before this hand-off existed */
  }
}

/**
 * Appends to what the tab already holds for the same draft: replaying an
 * operation twice ends in the same rows, so a later snapshot after an older one
 * is never wrong, and replacing could drop one never replayed (§D1).
 */
export function handOffUnsaved(entry: DraftHandOff): void {
  const held = read();
  const previous = held.find((candidate) => sameTarget(candidate, entry));
  const merged: DraftHandOff = previous
    ? {
        ...entry,
        operations: [...previous.operations, ...entry.operations],
        defaultReasons: entry.defaultReasons ?? previous.defaultReasons,
      }
    : entry;
  write([...held.filter((candidate) => !sameTarget(candidate, entry)), merged]);
}

/** Removes and returns the entries `matches` accepts. */
export function takeHandOffs(matches: (entry: DraftHandOff) => boolean): DraftHandOff[] {
  const held = read();
  const taken = held.filter(matches);
  if (taken.length > 0) write(held.filter((entry) => !matches(entry)));
  return taken;
}
